/**
 * Was ein Deep Link (`#/w=…`) mit den offenen Fenstern macht (#1188).
 *
 * Reine Planung, damit sie ohne Fenstersystem prüfbar ist; `useDeepLink`
 * führt den Plan aus.
 *
 * Zwei Fälle:
 *
 * - `apply` — die Adresse beim Laden der Seite. Sie ist ein Wunsch („zeig mir
 *   dieses Ticket“), kein vollständiger Fensterstand: Ein geteilter Link oder
 *   der Sprung aus `/chat` nennt nur sein Ziel. Andere Fenster bleiben offen.
 * - `reconcile` — eine Hash-Änderung im laufenden Desktop (Adresszeile,
 *   Zurück/Vor). Die Adresse ist der ganze Stand; was sie nicht nennt, schließt.
 *
 * In beiden Fällen gilt dieselbe Wiederverwendung wie beim Klick auf eine
 * Benachrichtigung (#1069): Ist die App schon offen, wird ihr vorderstes
 * Fenster auf das Ziel umgestellt und fokussiert, statt ein zweites zu öffnen.
 * Ein Ziel, das ein Objekt benennt (`documentId`, `recordingId`), nimmt nur
 * das Fenster genau dieses Objekts — ein fremdes Dokumentfenster umzubiegen,
 * tauschte aus, woran jemand gerade arbeitet.
 */
import type { WindowParams } from '@/types/window'
import { reusableWindow, windowIdentityKey } from '@/lib/window-identity'

export type DeepLinkSyncMode = 'apply' | 'reconcile'

export interface DeepLinkTarget {
  appId: string
  params?: WindowParams
}

export interface DeepLinkSyncWindow {
  id: string
  appId: string
  params?: WindowParams
  zIndex: number
  minimized: boolean
}

export interface DeepLinkSyncPlan {
  /** Fenster, die die Adresse nicht (mehr) nennt — nur bei `reconcile`. */
  close: string[]
  /** Offene Fenster, die auf ein Ziel umgestellt und fokussiert werden. */
  repoint: { windowId: string; params: WindowParams }[]
  /** Minimierte Fenster, die genau dem Ziel entsprechen und sichtbar werden (#516). */
  restore: string[]
  /** Ziele ohne passendes offenes Fenster. */
  open: DeepLinkTarget[]
}

function serializeParams(params?: WindowParams): string {
  if (!params) return '{}'
  const sortedEntries = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
  return JSON.stringify(Object.fromEntries(sortedEntries))
}

/** Gleiche App und gleiche Params — der Untertitel zählt nicht dazu. */
function sameTarget(window: DeepLinkSyncWindow, target: DeepLinkTarget): boolean {
  return window.appId === target.appId && serializeParams(window.params) === serializeParams(target.params)
}

/** Das noch freie Fenster, das ein nicht exakt getroffenes Ziel übernehmen darf. */
function windowFor(target: DeepLinkTarget, free: DeepLinkSyncWindow[]): DeepLinkSyncWindow | undefined {
  const identity = windowIdentityKey(target.appId, target.params)
  if (identity) return free.find((w) => windowIdentityKey(w.appId, w.params) === identity)
  return reusableWindow(free, target.appId, target.params)
}

/**
 * Bei `reconcile` ist die Adresse der ganze Stand: Params, die sie nicht
 * nennt, fallen weg (`undefined` löscht in `updateWindowParams`). Bei `apply`
 * ergänzt das Ziel die Params wie beim Klick auf eine Benachrichtigung.
 */
function paramsFor(window: DeepLinkSyncWindow, target: DeepLinkTarget, mode: DeepLinkSyncMode): WindowParams {
  const params = target.params ?? {}
  if (mode === 'apply') return params
  const cleared = Object.fromEntries(Object.keys(window.params ?? {}).map((key) => [key, undefined]))
  return { ...cleared, ...params }
}

export function planDeepLinkSync(
  targets: DeepLinkTarget[],
  windows: DeepLinkSyncWindow[],
  mode: DeepLinkSyncMode,
): DeepLinkSyncPlan {
  const plan: DeepLinkSyncPlan = { close: [], repoint: [], restore: [], open: [] }
  let free = [...windows]
  const claim = (id: string) => {
    free = free.filter((w) => w.id !== id)
  }

  // Exakte Treffer zuerst, damit ein Ziel nicht das Fenster belegt, das ein
  // späteres Ziel genau so schon zeigt.
  const unmatched: DeepLinkTarget[] = []
  for (const target of targets) {
    const exact = free.find((w) => sameTarget(w, target))
    if (!exact) {
      unmatched.push(target)
      continue
    }
    claim(exact.id)
    if (exact.minimized) plan.restore.push(exact.id)
  }

  for (const target of unmatched) {
    const existing = windowFor(target, free)
    if (!existing) {
      plan.open.push(target)
      continue
    }
    claim(existing.id)
    plan.repoint.push({ windowId: existing.id, params: paramsFor(existing, target, mode) })
  }

  if (mode === 'reconcile') plan.close = free.map((w) => w.id)
  return plan
}
